import Stepper from "@/components/common/Stepper";
import { AdminCreateUserWizardNav } from "@/features/admin/components/AdminCreateUserWizardNav";
import { useCreateUserWizardAccessState } from "@/features/admin/hooks/useCreateUserWizardAccessState";
import { getCreateUserWizardSteps, validateCreateUserWizardSearch } from "@/features/admin/utils/createUserWizard";
import { useSaveUserAccess } from "@/features/clinical-access/api/saveUserAccess";
import type { MockUser, UserAccess } from "@/features/clinical-access/types";
import { useSelectFormsLite } from "@/features/forms/api/selectForms";
import { useProfileByIdQuerySafe } from "@/features/profile/api/selectProfile";
import { templateQueryOptions } from "@/features/templates/api/selectTemplates";
import { TemplatesMultiSelectDialog } from "@/features/templates/components/TemplatesMultiSelectDialog";
import { useAuth } from "@/utils/auth";
import { createFileRoute, redirect, useNavigate } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";

export const Route = createFileRoute("/_app/users/create-user/templates")({
    validateSearch: validateCreateUserWizardSearch,
    beforeLoad: ({ search }) => {
        if (!search.userId) {
            throw redirect({ to: "/users/create-user/details" });
        }
    },
    component: CreateUserTemplatesStep,
});

function CreateUserTemplatesStep() {
    const search = Route.useSearch();
    const userId = search.userId!;
    const navigate = useNavigate();
    const auth = useAuth();
    const { data: profile } = useProfileByIdQuerySafe(userId);
    const saveUser = useSaveUserAccess();
    const { access, setAccess, baseline, setBaseline, overviewUser, isRemotePending, isHydrated } =
        useCreateUserWizardAccessState(userId);
    const isAdmin = auth?.roles?.isAdmin || auth?.isSuperUser;
    const { data: templates = [], isPending: templatesLoading } = useQuery(templateQueryOptions(auth.id, isAdmin));
    const { data: forms = [] } = useSelectFormsLite();

    const displayName =
        [profile?.first_name, profile?.last_name].filter(Boolean).join(" ") || profile?.email || "New user";

    const handleContinue = () => {
        // Preserve modes/grants from the access step; only update template grants.
        const accessToSave: UserAccess = { ...baseline, templateIds: access.templateIds };
        const next: MockUser = {
            id: userId,
            name: displayName,
            email: profile?.email ?? "",
            role: "CLINICIAN",
            organisation: overviewUser?.organisation ?? "",
            access: accessToSave,
            hasCustomOptions: false,
            source: "admin",
        };

        saveUser.mutate(
            { next, previousAccess: baseline },
            {
                onSuccess: () => {
                    setBaseline(accessToSave);
                    void navigate({
                        to: "/users/create-user/billing",
                        search: { userId },
                    });
                },
            }
        );
    };

    return (
        <div className="space-y-6" data-testid="create-user-templates-step">
            <Stepper steps={getCreateUserWizardSteps("templates", search)} />
            <div className="space-y-2">
                <h2 className="font-medium text-lg">Assign templates</h2>
                <p className="text-muted-foreground text-sm">
                    Pick diary setups {displayName} can use. This shares access to existing templates - it does not make
                    them the owner.
                </p>
            </div>

            {isRemotePending || !isHydrated ? (
                <div className="flex justify-center py-10">
                    <Loader2 className="w-8 h-8 text-muted-foreground animate-spin" />
                </div>
            ) : (
                <TemplatesMultiSelectDialog
                    label="Diary setup templates"
                    value={access.templateIds}
                    onValueChange={(templateIds) => setAccess((prev) => ({ ...prev, templateIds }))}
                    templates={templates}
                    forms={forms}
                    templatesLoading={templatesLoading}
                    placeholder="Select diary setups this user can use"
                    dialogTitle="Select templates"
                />
            )}

            <AdminCreateUserWizardNav
                search={search}
                backTo="/users/create-user/access"
                nextLabel={access.templateIds.length === 0 ? "Skip templates" : "Continue"}
                nextLoading={saveUser.isPending}
                nextDisabled={!isHydrated || isRemotePending}
                onNextClick={handleContinue}
            />
        </div>
    );
}
